import { Controller } from "@hotwired/stimulus"
import { loadGoogleMaps } from "../lib/google_maps"

export default class extends Controller {
  static targets = ["input", "panel", "suggestions"]
  static values = { apiKey: String }

  connect() {
    this.boundClickOutside = this.closeOnClickOutside.bind(this)
    this.boundEscape = this.closeOnEscape.bind(this)
    document.addEventListener("click", this.boundClickOutside)
    document.addEventListener("keydown", this.boundEscape)

    this.debounceTimer = null
    loadGoogleMaps(this.apiKeyValue).then(() => {
      this.autocompleteService = new google.maps.places.AutocompleteService()
      this.sessionToken = new google.maps.places.AutocompleteSessionToken()
    })
  }

  disconnect() {
    document.removeEventListener("click", this.boundClickOutside)
    document.removeEventListener("keydown", this.boundEscape)
    clearTimeout(this.debounceTimer)
  }

  open() {
    this.panelTarget.classList.remove("hidden")
  }

  close() {
    this.panelTarget.classList.add("hidden")
  }

  search() {
    const query = this.inputTarget.value.trim()
    clearTimeout(this.debounceTimer)

    if (!query || !this.autocompleteService) {
      this.#renderSuggestions([])
      return
    }

    this.debounceTimer = setTimeout(() => this.#fetchPredictions(query), 200)
  }

  select(event) {
    event.preventDefault()
    this.inputTarget.value = event.currentTarget.dataset.value || ""
    this.close()
  }

  closeOnClickOutside(event) {
    if (!this.element.contains(event.target)) this.close()
  }

  closeOnEscape(event) {
    if (event.key === "Escape") this.close()
  }

  #fetchPredictions(query) {
    this.autocompleteService.getPlacePredictions(
      { input: query, sessionToken: this.sessionToken, types: ["geocode"] },
      (predictions, status) => {
        if (status !== google.maps.places.PlacesServiceStatus.OK || !predictions) {
          this.#renderSuggestions([])
          return
        }
        this.#renderSuggestions(predictions)
      },
    )
  }

  #renderSuggestions(predictions) {
    this.suggestionsTarget.innerHTML = ""
    if (predictions.length === 0) {
      this.close()
      return
    }

    predictions.forEach((p) => {
      const main = p.structured_formatting?.main_text ?? p.description
      const secondary = p.structured_formatting?.secondary_text ?? ""
      const li = document.createElement("li")
      li.innerHTML = `
        <button type="button"
                class="group w-full flex items-center gap-4 text-left cursor-pointer"
                data-action="click->search-bar-location#select"
                data-value="${this.#escape(main)}">
          <span class="flex items-center justify-center shrink-0 size-10 rounded-lg bg-surface-low text-primary-600 group-hover:bg-primary-600 group-hover:text-white transition-colors">
            <svg class="size-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.8" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M15 10.5a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z"/><path stroke-linecap="round" stroke-linejoin="round" d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1 1 15 0Z"/></svg>
          </span>
          <span class="flex flex-col min-w-0">
            <span class="text-sm font-semibold text-on-surface truncate">${this.#escape(main)}</span>
            <span class="text-xs text-on-surface-variant truncate">${this.#escape(secondary)}</span>
          </span>
        </button>
      `
      this.suggestionsTarget.appendChild(li)
    })
    this.open()
  }

  #escape(str) {
    return String(str ?? "").replace(/[&<>"']/g, (c) => (
      { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]
    ))
  }
}
